import {Injectable} from "@angular/core";
import {RaVersion} from "../configuration/ra.version";
import {ContentServerService} from "./content.server.service";
import {Severity} from "../../constants";

@Injectable()
export class ClientConfiguration {

    // all paths on amazon s3
    private readonly VIDEOPATH = "/assets/video/";
    private readonly AUDIOPATH = "/assets/audio/";
    private readonly IMAGEPATH = "/assets/images_content/";
    private readonly CONTENTPATH = "/selections";

    // all path on slc
    public readonly APP_CONFIG_RESOURCE = "/ra/config/";
    public readonly USER_MODEL_RESOURCE= "/services/ra/learner/";
    public readonly RA_CREDENTIALS_PATH = "/ra/learner/credentials";
    public readonly RA_REST_SERVICE_PATH = "/services/RARESTService/";

    // stuff that is sucked in from a property file
    public logLevel: Severity = Severity.ERROR;
    public s3Url: string;
    public contentServerUrl: string;
    public s3Directory: string;
    public keepAliveInterval: number;
    public debugKeys: boolean = false;
    public superUser: boolean = false;
    public superUserDev: boolean = false;
    public timeoutStopMode: boolean = true;
    public browserVersion: boolean = true;
    public microphoneConnectedCheck: boolean;
    public featureFlag: boolean = false;
    public featureTickets: string[] = [];
    public contentToolServerURL: string;
    private clearFluencyServer: string;

    // derived properties
    public audioUrl: string;
    public videoUrl: string;
    public imageUrl: string;
    public contentUrl: string;
    public contentServerImageUrl: string;
    private slcLocation: string;
    public helpUrl: string;


    private determineContentServerUrl(url: string): string {
        if (ContentServerService.isContentServerActive()) {
            return ContentServerService.determineContentServer();
        } else {
            return url;
        }
    }

    public addProps(props): void {

        if (props["logLevel"] != null) {
            console.log("loading logLevel=", props["logLevel"]);
            this.logLevel = props["logLevel"] as Severity;
        }
        if (props["superUser"] != null) {
            console.log("loading superuser=", props["superUser"]);
            this.superUser = Boolean(props["superUser"]);
        }

        if (props["debugKeys"] != null) {
            console.log("loading debugKeys=", props["debugKeys"]);
            this.debugKeys = Boolean(props["debugKeys"]);
        }

        if (props["browserVersion"] != null) {
            console.log("loading browserVersion=", props["browserVersion"]);
            this.browserVersion = Boolean(props["browserVersion"]);
        }
        if (props["timeoutStopMode"] != null) {
            console.log("loading timeoutStopMode=", props["timeoutStopMode"]);
            this.timeoutStopMode = Boolean(props["timeoutStopMode"]);
        }
        if (props["microphoneConnectedCheck"] != null) {
            console.log("loading microphoneConnectedCheck=", props["microphoneConnectedCheck"]);
            this.microphoneConnectedCheck = Boolean(props["microphoneConnectedCheck"]);
        }
        if (props["featureFlag"] != null) {
            console.log("loading featureFlag=", props["featureFlag"]);
            this.featureFlag = Boolean(props["featureFlag"]);
        }
        if (props["featureTickets"] != null) {
            console.log("loading featureTickets=", props["featureTickets"]);
            this.featureTickets = props["featureTickets"];
        }
        if (props["keepAliveInterval"] != null) {
            console.log("loading keepAliveInterval=", props["keepAliveInterval"]);
            this.keepAliveInterval = Number(props["keepAliveInterval"]);
        }

        if (props["s3Directory"] != null) {
            this.s3Directory = props["s3Directory"];
        }

        if (props["s3Url"] != null) {
            console.log("loading s3Url=", props["s3Url"]);
            this.s3Url = props["s3Url"];
            this.contentServerUrl = this.determineContentServerUrl(props["s3Url"]);
            console.log("loading contentServerUrl=", this.contentServerUrl);

            // We need both image urls here because for certain images like the library images
            // and app images we still need to default to the s3 url no matter what. We are not
            // able to dynamically switch the css asset image paths since they’re determined
            // statically during compile time for the app. Selection images and some other ones
            // noted below are the only ones that can possibly use the content server image urls.
            this.imageUrl = this.s3Url + this.IMAGEPATH;
            this.contentServerImageUrl = this.contentServerUrl + this.IMAGEPATH;

            this.contentUrl = this.s3Url + "/" + this.s3Directory + this.CONTENTPATH;
            this.audioUrl = this.contentServerUrl + this.AUDIOPATH;
            this.videoUrl = this.contentServerUrl + this.VIDEOPATH;
        }

        if (props["helpUrl"] != null) {
            console.log("loading helpUrl=", props["helpUrl"]);
            this.helpUrl = props["helpUrl"];
        }

        if (props["clearFluencyServer"] != null) {
            console.log("loading clearFluencyServer=", props["clearFluencyServer"]);
            this.clearFluencyServer = props["clearFluencyServer"];
        }
    }

    public getKeepAliveInterval(): number {
        return this.keepAliveInterval;
    }

    public get rootPath(): string {
        return "/rav2ng/";
    }

    public setSlcLocation(slcLocation: string): void {
        this.slcLocation = slcLocation;
    }

    public get slcPath(): string {
        return this.slcLocation;
    }

    public get slcReferrerDomain(): string {
        const slcURL = new URL(this.slcLocation);
        return slcURL.host;
    }

    public get allowDebugKeys(): boolean {
        return this.debugKeys;
    }

    public get isSuperUserMode(): boolean {
        return this.superUser;
    }

    public get isSuperUserDevMode(): boolean {
        return this.superUserDev;
    }

    public set setSuperUserMode(isSuperUser: boolean) {
        this.superUser = isSuperUser;
    }

    public set setSuperUserDevMode(isSuperUserDev: boolean) {
        this.superUserDev = isSuperUserDev;
    }

    public get allowTimeoutStopMode(): boolean {
        return this.timeoutStopMode;
    }

    public get allowBrowserVersion(): boolean {
        return this.browserVersion;
    }

    public get allowMicrophoneConnectCheck(): boolean {
        return this.microphoneConnectedCheck;
    }

    public get credentialsPath(): string {
        return this.baseAPIPath + this.RA_CREDENTIALS_PATH;
    }

    public get keepAlivePath(): string {
        return this._raRestUrl + "TrainingSessions/";
    }

    public get assignmentPath():string {
        return this._raRestUrl + "Assignments/v2/";
    }

    public get speechPath():string {
        return this.baseAPIPath + this.USER_MODEL_RESOURCE;
    }

    public get userModelPath():string {
        return this.baseAPIPath + this.USER_MODEL_RESOURCE;
    }

    public get trainingPath():string {
        return this._raRestUrl + "TrainingSessions/";
    }

    public get selectionReportPath(): string {
        return this.baseAPIPath + this.USER_MODEL_RESOURCE + "SelectionReport/";
    }

    public get pointsReportPath(): string {
        return this.baseAPIPath + this.USER_MODEL_RESOURCE + "PointsReport/";
    }

    public get fluencyReportPath(): string {
        return this.baseAPIPath + this.USER_MODEL_RESOURCE + "FluencyReport/";
    }

    public get propsPath(): string {
        return this.baseAPIPath + this.APP_CONFIG_RESOURCE;
    }

    private get _raRestUrl(): string {
        return this.baseAPIPath + this.RA_REST_SERVICE_PATH;
    }

    public get clearFluencyServerPath(): string {
        return this.clearFluencyServer;
    }

    public get baseAPIPath(): string {
        return !!this.clearFluencyServer ? this.clearFluencyServer : this.slcLocation;
    }

    public selectionImageUrl(url: string): string {
        return this.contentServerImageUrl + url + RaVersion.full;
    }

    public libraryImageUrl(url: string): string {
        if ( window.devicePixelRatio > 1) {
            return this.imageUrl + "intro/" + url + RaVersion.full;
        } else {
            return this.imageUrl + "library/" + url + RaVersion.full;
        }
    }

    public introImageUrl(url: string): string {
        return this.contentServerImageUrl + "intro/" + url + RaVersion.full;
    }

    public pointsImageUrl(url: string): string {
        return this.contentServerImageUrl + "points/" + url + RaVersion.full;
    }

    public parseVideoImageUrl(url: string, timestamp: number): string {
        return this.contentServerImageUrl + url + "?v=" + timestamp;
    }

    public parseVideoUrl(url: string, timestamp: number): string {
        return this.videoUrl + url + "?v=" + timestamp;
    }

    public getApplicationAudioPath(): string {
        return this.audioUrl;
    }

    public getHelpUrl(): string {
        return this.helpUrl;
    }


    public getContentToolServerURL(): string {
        return this.contentToolServerURL;
    }

    public setContentToolServerURL(contentToolServerURL: string): void {
        this.contentToolServerURL = contentToolServerURL;
    }

}
